/* eslint-disable css-modules/no-unused-class */
import { FC, useRef, useEffect, useState } from 'react';
import Editor from '@monaco-editor/react';
import classNames from 'classnames';
import styles from './customEditor.module.scss';
import { CustomEditorProps, EditorTheme } from './customEditorTypes';
import { useTranslationContext } from '../contexts/translationContext';

const CustomEditor: FC<CustomEditorProps> = (props: CustomEditorProps) => {
    const {
        readOnly,
        code,
        onCodeChange,
        codeSections = [],
        language = 'javascript',
        theme = EditorTheme.VSDARK,
        id
    } = props;

    const [isRefSet, setIsRefSet] = useState(false);
    const [decorationIds, setDecorationIds] = useState<string[]>([]);

    const { translate } = useTranslationContext();

    const START_TYPING = translate('StartTyping');

    const editorRef = useRef<any>(null);
    const monacoRef = useRef<any>(null);

    const editorContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (editorRef.current) {
                const viewOverlays = document.querySelectorAll('.view-overlays');

                viewOverlays.forEach(viewOverlay => {
                    viewOverlay.querySelectorAll(':scope > div').forEach(div => {
                        if (!div.querySelector('.decoration')) {
                            div.classList.add('no-decoration');
                        }
                    });
                });
            }
        });

        if (editorContainerRef.current) {
            observer.observe(editorContainerRef.current, { childList: true, subtree: true });
        }

        return () => observer.disconnect();
    }, []);

    // Prevent copy/paste/cut events
    useEffect(() => {
        const handleCopy = (e: ClipboardEvent) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };

        const handlePaste = (e: ClipboardEvent) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };

        const handleCut = (e: ClipboardEvent) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };

        const container = editorContainerRef.current;
        if (container) {
            container.addEventListener('copy', handleCopy, true);
            container.addEventListener('paste', handlePaste, true);
            container.addEventListener('cut', handleCut, true);
        }

        return () => {
            if (container) {
                container.removeEventListener('copy', handleCopy, true);
                container.removeEventListener('paste', handlePaste, true);
                container.removeEventListener('cut', handleCut, true);
            }
        };
    }, []);

    useEffect(() => {
        if (editorRef.current && monacoRef.current) {
            const editor = editorRef.current;
            const monaco = monacoRef.current;

            const newDecorations = codeSections.flatMap(section => {
                const { isCorrect } = section;
                if (section.startLine === section.endLine) {
                    return [
                        {
                            range: new monaco.Range(section.startLine, 1, section.startLine, 1),
                            options: {
                                isWholeLine: true,
                                className: `decoration${isCorrect ? ' singleLineBoxCorrect' : ' singleLineBox'}`,
                                hoverMessage: { value: section.description, isTrusted: true }
                            }
                        }
                    ];
                }

                return [
                    {
                        range: new monaco.Range(section.startLine, 1, section.startLine, 1),
                        options: {
                            isWholeLine: true,
                            className: `decoration${isCorrect ? ' startLineBoxCorrect' : ' startLineBox'}`,
                            hoverMessage: { value: section.description, isTrusted: true }
                        }
                    },
                    ...Array(section.endLine - section.startLine - 1)
                        .fill(0)
                        .map((_, index) => ({
                            range: new monaco.Range(section.startLine + index + 1, 1, section.startLine + index + 1, 1),
                            options: {
                                isWholeLine: true,
                                className: `decoration${isCorrect ? ' middleLineBoxCorrect' : ' middleLineBox'}`,
                                hoverMessage: { value: section.description, isTrusted: true }
                            }
                        })),
                    {
                        range: new monaco.Range(section.endLine, 1, section.endLine, 1),
                        options: {
                            isWholeLine: true,
                            className: `decoration${isCorrect ? ' endLineBoxCorrect' : ' endLineBox'}`,
                            hoverMessage: { value: section.description, isTrusted: true }
                        }
                    }
                ];
            });
            const newDecorationIds = editor.deltaDecorations(decorationIds, newDecorations);
            setDecorationIds(newDecorationIds);
        }
    }, [isRefSet, codeSections]);

    function handleEditorChange(value: string | undefined, _event: any) {
        if (value !== undefined) {
            onCodeChange?.(value);
        }
    }

    function handleEditorDidMount(editor, monaco) {
        editorRef.current = editor;
        monacoRef.current = monaco;
        setIsRefSet(true);
    }

    const isLinesOpacity = codeSections.length > 0;

    return (
        <div
            id={id}
            className={classNames(styles.container, {
                [styles['lines-opacity']]: isLinesOpacity,
                [styles['read-only']]: readOnly
            })}
            ref={editorContainerRef}
        >
            {code.length === 0 && !readOnly && <div className={styles.placeholder}>{START_TYPING}</div>}
            <Editor
                defaultLanguage={language}
                theme={theme}
                onChange={handleEditorChange}
                onMount={handleEditorDidMount}
                value={code}
                options={{
                    minimap: { enabled: false },
                    readOnly,
                    selectionClipboard: false,
                    copyWithSyntaxHighlighting: false,
                    contextmenu: false,
                    domReadOnly: true
                }}
            />
        </div>
    );
};

export default CustomEditor;
